import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { FacetGroup } from './shared/facetTypes';
import { fetchAccountFacets, fetchAccounts } from './accountsApi';
import {
    EMPTY_REALM_FILTERS,
    type AccountSortDirection,
    type AccountSortField,
    type RealmAccountsFilters,
} from './types';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

function orderingParam(field: AccountSortField, direction: AccountSortDirection): string {
    return direction === 'desc' ? `-${field}` : field;
}

/**
 * EAA-1820: the realm-wide accounts screen's data — everything
 * `useAccountsScan.ts` + `useAccountsView.ts` used to compute over a
 * capped-2,000-row client walk, now driven by the two REAL endpoints
 * `GET /api/v1/accounts` (list, real server pagination/search/filter/sort) and
 * `GET /api/v1/accounts/facets` (rail counts + the four saved-filter counts).
 *
 * `useAccountsScan.ts` is DELETED, not kept as a fallback: it under-counted
 * silently past its 2,000-row cap, and the facets endpoint answers the same
 * question exactly, in one request, for the whole realm (also honestly capped —
 * see `capped` below — but computed server-side rather than by an unbounded
 * client walk).
 */
export function useRealmAccountsAdapter(apiBase: string, accessToken: string | null, enabled: boolean) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState<RealmAccountsFilters>(EMPTY_REALM_FILTERS);
    const [sortField, setSortField] = useState<AccountSortField>('created');
    const [sortDirection, setSortDirection] = useState<AccountSortDirection>('desc');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [selected, setSelected] = useState<Set<string>>(new Set());


    const listQuery = useQuery({
        queryKey: [
            'console-accounts-list',
            accessToken,
            search,
            filters,
            sortField,
            sortDirection,
            page,
            pageSize,
        ],
        queryFn: () =>
            fetchAccounts(apiBase, accessToken as string, {
                page,
                page_size: pageSize,
                search,
                status: filters.status ?? undefined,
                permission: filters.permission ?? undefined,
                organization_id: filters.organizationId ?? undefined,
                source: filters.source ?? undefined,
                ordering: orderingParam(sortField, sortDirection),
            }),
        enabled: enabled && Boolean(accessToken),
        retry: false,
        staleTime: 15_000,
    });

    // EAA-1820: the rail's counts come from a SEPARATE aggregate endpoint that
    // only takes `search` — narrowing by the rail's own facets is therefore not
    // possible server-side (there is no per-facet-combination count endpoint,
    // same limitation the org screen already has — see `useScopedAccountFacets`).
    // Each group's counts describe "how many match the current SEARCH", not "how
    // many would this option add given the other ticked facets"; that is the one
    // honest simplification this screen makes relative to the old client walk,
    // which could recompute in memory.
    const facetsQuery = useQuery({
        queryKey: ['console-accounts-facets', accessToken, search],
        queryFn: () => fetchAccountFacets(apiBase, accessToken as string, search),
        enabled: enabled && Boolean(accessToken),
        retry: false,
        staleTime: 15_000,
    });

    const rows = listQuery.data?.results ?? [];
    const total = listQuery.data?.count ?? 0;
    const totalPages = Math.max(1, listQuery.data?.total_pages ?? 1);
    const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const rangeTo = Math.min(page * pageSize, total);
    const lastLoginAvailable = listQuery.data?.last_login_available ?? false;
    const facets = facetsQuery.data ?? null;
    const capped = Boolean(listQuery.data?.capped) || Boolean(facets?.capped);

    const invalidate = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: ['console-accounts-list'] });
        void queryClient.invalidateQueries({ queryKey: ['console-accounts-facets'] });
    }, [queryClient]);

    const patchFilters = useCallback((patch: Partial<RealmAccountsFilters>) => {
        setFilters((prev) => ({ ...prev, ...patch }));
        setPage(1);
    }, []);

    const hasActiveFilters =
        Boolean(search.trim()) ||
        filters.status !== null ||
        filters.permission !== null ||
        filters.source !== null ||
        filters.organizationId !== null;

    const activeFilterCount =
        (search.trim() ? 1 : 0) +
        (filters.status !== null ? 1 : 0) +
        (filters.permission !== null ? 1 : 0) +
        (filters.source !== null ? 1 : 0) +
        (filters.organizationId !== null ? 1 : 0);

    const clearFilters = useCallback(() => {
        setSearch('');
        setFilters(EMPTY_REALM_FILTERS);
        setPage(1);
    }, []);

    const selectFacet = useCallback(
        (groupKey: string, optionKey: string) => {
            if (groupKey === 'status') {
                patchFilters({ status: filters.status === optionKey ? null : (optionKey as never) });
            } else if (groupKey === 'permission') {
                patchFilters({
                    permission: filters.permission === optionKey ? null : (optionKey as never),
                });
            } else if (groupKey === 'source') {
                patchFilters({ source: filters.source === optionKey ? null : (optionKey as never) });
            } else if (groupKey === 'organization') {
                patchFilters({
                    organizationId: filters.organizationId === optionKey ? null : optionKey,
                });
            }
        },
        [filters, patchFilters]
    );

    const facetGroups: FacetGroup[] = useMemo(() => {
        const c = facets ?? {
            status: { active: 0, pending: 0, locked: 0 },
            permission: { granted: 0, none: 0 },
            source: { console: 0, unknown: 0 },
            organizations: [] as { organization_id: string; organization_name: string; count: number }[],
        };
        return [
            {
                key: 'status',
                label: t('console.accounts.facetRail.statusGroupTitle'),
                options: [
                    {
                        key: 'active',
                        label: t('console.accounts.facetRail.statusEnabled'),
                        count: facets ? c.status.active : null,
                        selected: filters.status === 'active',
                    },
                    {
                        key: 'pending',
                        label: t('console.accounts.facetRail.statusPending'),
                        count: facets ? c.status.pending : null,
                        selected: filters.status === 'pending',
                    },
                    {
                        key: 'locked',
                        label: t('console.accounts.facetRail.statusDisabled'),
                        count: facets ? c.status.locked : null,
                        selected: filters.status === 'locked',
                    },
                ],
            },
            {
                key: 'permission',
                label: t('console.accounts.facetRail.permissionGroupTitle'),
                options: [
                    {
                        key: 'granted',
                        label: t('console.accounts.facetRail.permissionGranted'),
                        count: facets ? c.permission.granted : null,
                        selected: filters.permission === 'granted',
                    },
                    {
                        key: 'none',
                        label: t('console.accounts.facetRail.permissionNone'),
                        count: facets ? c.permission.none : null,
                        selected: filters.permission === 'none',
                    },
                ],
            },
            {
                key: 'organization',
                label: t('console.accounts.facetRail.organizationGroupTitle'),
                options: c.organizations.map((org) => ({
                    key: org.organization_id,
                    label: org.organization_name,
                    count: org.count,
                    selected: filters.organizationId === org.organization_id,
                })),
            },
            {
                key: 'source',
                label: t('console.accounts.facetRail.sourceGroupTitle'),
                options: [
                    {
                        key: 'console',
                        label: t('console.accounts.facetRail.sourceConsole'),
                        count: facets ? c.source.console : null,
                        selected: filters.source === 'console',
                    },
                    {
                        key: 'unknown',
                        label: t('console.accounts.facetRail.sourceUnknown'),
                        count: facets ? c.source.unknown : null,
                        selected: filters.source === 'unknown',
                    },
                ],
            },
        ];
    }, [facets, filters, t]);


    function onSort(field: AccountSortField) {
        if (field === sortField) {
            setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection(field === 'created' ? 'desc' : 'asc');
        }
        setPage(1);
    }

    return {
        search,
        setSearch: (value: string) => {
            setSearch(value);
            setPage(1);
        },
        filters,
        hasActiveFilters,
        activeFilterCount,
        clearFilters,
        selectFacet,
        facetGroups,
        rows,
        total,
        capped,
        lastLoginAvailable,
        listQuery,
        facetsQuery,
        invalidate,

        sortField,
        sortDirection,
        onSort,

        page,
        setPage,
        pageSize,
        setPageSize: (size: number) => {
            setPageSize(size);
            setPage(1);
        },
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        totalPages,
        rangeFrom,
        rangeTo,

        selected,
        setSelected,
    };
}

export type RealmAccountsAdapter = ReturnType<typeof useRealmAccountsAdapter>;
